import React, { useEffect, useState } from 'react';
import { Scale, Loader2, CheckCircle2, FileEdit } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { Rateio } from '../../../types';
import { marcarConciliado } from './protocoloActions';
import RegistrarEspelhoModal from './RegistrarEspelhoModal';

const TOLERANCIA_PERCENT = 0.5;

type RateioComUg = Rateio & {
  rateio_unidades_geradoras: { nome: string; distribuidora_config: { nome: string } | null } | null;
};

const formatKwh = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Tela 4 — Conciliação e Monitoramento. Entrada do espelho é manual (Seção 7
 * — não há mecanismo de leitura automática definido na spec); a partir do
 * total informado, calcula a divergência % e aplica a tolerância de 0,5%
 * (Seção 13.4). "Marcar como Conciliado" é uma decisão do Gestor, mesmo fora
 * da tolerância — o sistema só sinaliza, não bloqueia.
 */
const ConciliacaoView: React.FC = () => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const isGestor = profile?.role === 'adm' || profile?.role === 'gerente';

  const [loading, setLoading] = useState(true);
  const [rateios, setRateios] = useState<RateioComUg[]>([]);
  const [espelhoRateio, setEspelhoRateio] = useState<RateioComUg | null>(null);
  const [conciliandoId, setConciliandoId] = useState<string | null>(null);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('rateios')
        .select('*, rateio_unidades_geradoras(nome, distribuidora_config(nome))')
        .in('status', ['protocolado', 'conciliado'])
        .order('protocolado_em', { ascending: false });
      if (error) throw error;
      setRateios((data || []) as RateioComUg[]);
    } catch (error) {
      console.error('Error loading conciliacao', error);
      addToast('Erro ao carregar dados de conciliação.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConciliar = async (rateio: RateioComUg) => {
    setConciliandoId(rateio.id);
    try {
      await marcarConciliado(rateio.id);
      addToast('Rateio marcado como conciliado.', 'success');
      carregar();
    } catch (error: any) {
      console.error('Error marking conciliado', error);
      addToast(error?.message || 'Erro ao marcar como conciliado.', 'error');
    } finally {
      setConciliandoId(null);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="p-6 border-b border-gray-100 dark:border-gray-700">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white font-poppins flex items-center gap-2">
          <Scale className="text-primary" size={20} /> Conciliação e Monitoramento
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Divergências entre o excedente calculado pelo sistema e o excedente faturado pela distribuidora. Entrada do
          espelho é manual — tolerância de {TOLERANCIA_PERCENT}%.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-gray-400" size={28} />
        </div>
      ) : rateios.length === 0 ? (
        <div className="p-16 text-center">
          <Scale className="mx-auto text-gray-300 dark:text-gray-600 mb-3" size={40} />
          <p className="text-gray-500 dark:text-gray-400">Nenhum rateio protocolado aguardando conciliação ainda.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-gray-700">
                <th className="py-2 px-6">UG</th>
                <th className="py-2 px-6">Distribuidora</th>
                <th className="py-2 px-6">Mês</th>
                <th className="py-2 px-6">Excedente Calculado</th>
                <th className="py-2 px-6">Excedente Faturado</th>
                <th className="py-2 px-6">Divergência</th>
                <th className="py-2 px-6 text-right">Ação</th>
              </tr>
            </thead>
            <tbody>
              {rateios.map(rateio => {
                const dentroTolerancia = rateio.divergencia_percent != null && rateio.divergencia_percent <= TOLERANCIA_PERCENT;
                return (
                  <tr key={rateio.id} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 px-6 font-medium text-gray-900 dark:text-gray-100">
                      {rateio.rateio_unidades_geradoras?.nome || '—'}
                    </td>
                    <td className="py-3 px-6 text-gray-600 dark:text-gray-300">
                      {rateio.rateio_unidades_geradoras?.distribuidora_config?.nome || '—'}
                    </td>
                    <td className="py-3 px-6 text-gray-600 dark:text-gray-300">{rateio.mes_referencia}</td>
                    <td className="py-3 px-6 text-gray-600 dark:text-gray-300">{formatKwh(rateio.excedente_alocavel_kwh)} kWh</td>
                    <td className="py-3 px-6 text-gray-600 dark:text-gray-300">
                      {rateio.excedente_faturado_kwh != null ? `${formatKwh(rateio.excedente_faturado_kwh)} kWh` : '—'}
                    </td>
                    <td className="py-3 px-6">
                      {rateio.divergencia_percent == null ? (
                        <span className="text-gray-400">Aguardando espelho</span>
                      ) : (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            dentroTolerancia
                              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                              : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                          }`}
                        >
                          {rateio.divergencia_percent.toFixed(2)}% {dentroTolerancia ? '· dentro da tolerância' : '· fora da tolerância'}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-6 text-right">
                      {isGestor && (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setEspelhoRateio(rateio)}
                            className="flex items-center gap-1 text-sm text-primary hover:underline"
                          >
                            <FileEdit size={14} /> {rateio.excedente_faturado_kwh != null ? 'Editar Espelho' : 'Registrar Espelho'}
                          </button>
                          {rateio.status === 'protocolado' && (
                            <button
                              onClick={() => handleConciliar(rateio)}
                              disabled={conciliandoId === rateio.id}
                              className="flex items-center gap-1 text-sm text-emerald-700 dark:text-emerald-400 hover:underline disabled:opacity-60"
                            >
                              {conciliandoId === rateio.id ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <CheckCircle2 size={14} />
                              )}
                              Marcar como Conciliado
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {espelhoRateio && (
        <RegistrarEspelhoModal
          rateio={espelhoRateio}
          ugNome={espelhoRateio.rateio_unidades_geradoras?.nome || ''}
          onClose={() => setEspelhoRateio(null)}
          onSaved={() => {
            setEspelhoRateio(null);
            carregar();
          }}
        />
      )}
    </div>
  );
};

export default ConciliacaoView;
